import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";

const HERO_IMG =
  "https://images.pexels.com/photos/10410019/pexels-photo-10410019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CTA_IMG =
  "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHIPS = ["Founded 2003", "Public company", "iStar RTK engine", "MCSite cloud"];

const STATS = [
  ["2003", "Founded in Shanghai"],
  ["2,200+", "Staff worldwide"],
  ["1,000+", "R&D engineers"],
  ["140+", "Countries served"],
];

const CHAPTERS = [
  {
    n: "01",
    title: "The company",
    kicker: "Listed, audited, accountable",
    body: "CHCNAV — Shanghai Huace Navigation Technology — was founded in 2003 and has been a public company on the Shenzhen Stock Exchange since 2017 (ticker 300627). Audited financials, published filings and thousands of shareholders. This is not a pop-up importer: it's an established technology company you can look up, with the balance sheet to stand behind its warranty.",
    img: "https://images.unsplash.com/photo-1534097575056-ddba81f714c8?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Aerial view of excavator and dump truck on site",
  },
  {
    n: "02",
    title: "In-house everything",
    kicker: "From chip to cab",
    body: "More than 1,000 R&D engineers across research centres in Shanghai and Wuhan design their own GNSS boards, smart antennas, IMUs, displays and the proprietary iStar RTK engine. The hardware isn't bought in and badged — it's built from the component up, the same vertical integration the legacy brands built their names on.",
    img: "https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Surveyor with GNSS receiver on site",
  },
  {
    n: "03",
    title: "The software stack",
    kicker: "One system, not a patchwork",
    body: "MCSite cloud, iSite over-the-air updates, the office tools and the file formats your jobs already speak — LandXML, DWG, DXF. Because the software is written alongside the hardware it runs on, receivers, displays and the cloud move as one system instead of a bundle of somebody else's parts.",
    img: "/mcsite.png",
    alt: "CHCNAV MCSite cloud platform on a laptop screen",
    screen: true,
  },
  {
    n: "04",
    title: "Global scale",
    kicker: "A world leader, by the numbers",
    body: (
      <>
        CHCNAV sells into 140+ countries with subsidiaries in Europe and North America, and is the world's largest
        supplier of GNSS smart antennas — 100,000+ shipped every year. Four product lines — geospatial, machine
        control, precision agriculture and OEM positioning for autonomy — compete head-to-head with the{" "}
        <Link
          to="/#lineup"
          data-testid="precision-lineup-link"
          className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon"
        >
          systems you already know
        </Link>
        , at global scale.
      </>
    ),
    img: "https://images.unsplash.com/photo-1613928270169-4e4462c75a79?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Yellow excavator shaping grade on brown soil",
  },
  {
    n: "05",
    title: "On your site",
    kicker: "Why that matters in your digger",
    body: "A listed company stands behind every warranty. A thousand engineers stand behind every over-the-air update. And Green Grade stands behind the install — we spec the right kit for your fleet, fit it, train your operators and answer the phone long after delivery. Global engineering, local crew.",
    img: "https://images.unsplash.com/photo-1603816886392-25f1d28406d9?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Excavator bucket digging to grade",
  },
];

export default function PrecisionSolutions() {
  return (
    <main>
      <section data-testid="precision-hero" className="relative flex min-h-[88svh] items-end overflow-hidden">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Excavator working at sunset" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>CHCNAV Precision Solutions</Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["A global leader,", { text: "not a gamble.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            The machine control behind Green Grade is built by one of the world's largest precision positioning
            companies — designed, manufactured and programmed under one roof, and proven on worksites in 140+ countries.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span
                key={c}
                className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm"
              >
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="precision-demo-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/#quote"
              data-testid="precision-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-6 border-t border-line/70 pt-8 lg:grid-cols-4" data-testid="precision-stats">
            {STATS.map(([v, l], i) => (
              <FadeUp key={l} delay={0.4 + i * 0.1} y={16}>
                <div className="font-display text-4xl font-black text-white sm:text-5xl">{v}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-fog sm:text-[11px]">{l}</div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="precision-chapters" className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Why CHCNAV" title="Big enough to trust. Built to compete." />
          <div className="mt-20 space-y-24 lg:space-y-32">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.n}
                className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-16 ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
              >
                <div className="lg:col-span-2 lg:[direction:ltr]">
                  <FadeUp>
                    <span className="text-stroke font-display text-7xl font-black leading-none sm:text-8xl">{c.n}</span>
                  </FadeUp>
                </div>
                <div className="lg:col-span-5 lg:[direction:ltr]">
                  <FadeUp delay={0.08}>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">{c.kicker}</p>
                    <h3 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
                      {c.title}
                    </h3>
                    <p className="mt-6 max-w-lg text-base leading-relaxed text-fog sm:text-lg">{c.body}</p>
                  </FadeUp>
                </div>
                <div className="lg:col-span-5 lg:[direction:ltr]">
                  <FadeUp delay={0.16}>
                    {c.screen ? (
                      <div className="group" data-testid="precision-laptop-mockup">
                        <div className="rounded-t-xl border-4 border-b-0 border-slate-700 bg-slate-800 p-2">
                          <img
                            src={c.img}
                            alt={c.alt}
                            loading="lazy"
                            className="aspect-[16/9] w-full rounded-sm object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                          />
                        </div>
                        <div className="h-3 w-full rounded-b-xl bg-slate-700 shadow-lg" />
                        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-fog">
                          MCSite cloud — live on screen
                        </p>
                      </div>
                    ) : (
                      <div className="group relative overflow-hidden border border-line">
                        <img
                          src={c.img}
                          alt={c.alt}
                          loading="lazy"
                          className="clip-frame h-64 w-full object-cover grayscale-[30%] transition-transform duration-700 group-hover:scale-105 sm:h-72"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                      </div>
                    )}
                  </FadeUp>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-line" data-testid="precision-cta-banner">
        <img src={CTA_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
          <FadeUp>
            <h2 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-white sm:text-7xl lg:text-8xl">
              Global leader. <span className="italic text-neon">Local crew.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 max-w-md text-lg text-slate-300">
              See the full CHCNAV lineup on your own machine — demoed on your site, quoted with no surprises.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <Link
              to="/contact"
              data-testid="precision-cta-banner-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
